import { Listbox } from '@headlessui/react'
import { SearchIcon as MagnifyingGlassIcon } from 'lucide-react'
import type { Dispatch, RefObject, SetStateAction } from 'react'
import { cx } from './select/classNames'
import { SelectOptionRow } from './select/SelectOptionRow'
import type { SelectListboxLayoutProps, SelectOption } from './select/types'

/**
 * Props for the searchable select listbox, including the embedded search input
 * and the already-filtered options to render.
 */
export type SearchableSelectOptionsListProps = SelectListboxLayoutProps & {
  filteredOptions: SelectOption[]
  query: string
  setQuery: Dispatch<SetStateAction<string>>
  searchInputRef: RefObject<HTMLInputElement | null>
  searchPlaceholder: string
  emptyLabel: string
  emphasizePrimaryLabel: boolean
  optionsClassName?: string
}

/**
 * Listbox structure for searchable selects, including the sticky search field.
 */
export const SearchableSelectOptionsList = ({
  filteredOptions,
  query,
  setQuery,
  searchInputRef,
  searchPlaceholder,
  emptyLabel,
  emphasizePrimaryLabel,
  optionsClassName,
  style,
  placement,
  optionsRef,
}: SearchableSelectOptionsListProps) => (
  <Listbox.Options
    as="div"
    ref={optionsRef}
    style={style}
    className={cx(
      'fixed z-[240] overflow-y-auto overscroll-contain rounded-xl border p-1 text-sm shadow-[0_20px_40px_-28px_rgba(17,24,39,0.45)] focus:outline-none normal-case',
      placement === 'top' ? 'origin-bottom-left' : 'origin-top-left',
      'border-(--color-border-default,#e2e8f0) bg-(--color-bg-card,#ffffff) text-(--color-text-secondary,#334155)',
      optionsClassName,
    )}
  >
    <div className="sticky top-0 z-10 rounded-lg bg-(--color-bg-card,#ffffff) p-1">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-(--color-text-muted,#94a3b8)" />
        <input
          ref={searchInputRef}
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={searchPlaceholder}
          className="h-9 w-full rounded-lg border border-(--color-border-default,#e2e8f0) bg-(--color-bg-card,#ffffff) pl-8 pr-2 text-sm text-(--color-text-secondary,#334155) outline-none transition focus:border-(--color-focus-ring,#3b82f6) focus:ring focus:ring-[color-mix(in_srgb,var(--color-focus-ring,#3b82f6)_22%,transparent)]"
        />
      </div>
    </div>
    {filteredOptions.length === 0 ? (
      <div className="px-3 py-2 text-xs text-(--color-text-muted,#64748b)">{emptyLabel}</div>
    ) : (
      filteredOptions.map((option) => (
        <SelectOptionRow key={option.value} option={option} emphasizePrimaryLabel={emphasizePrimaryLabel} />
      ))
    )}
  </Listbox.Options>
)
